import { useTranslation } from 'react-i18next';
import { useTheme } from '@/theme/useTheme';

const FooterSection = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const navigationLinksRaw = t('home.footer.navigationLinks', { returnObjects: true });
  const navigationLinks = Array.isArray(navigationLinksRaw)
    ? (navigationLinksRaw as Array<{ label: string; href: string }>)
    : [];
  const communityLinksRaw = t('home.footer.communityLinks', { returnObjects: true });
  const communityLinks = Array.isArray(communityLinksRaw)
    ? (communityLinksRaw as Array<{ label: string; href: string; accent?: boolean }>)
    : [];
  const socialLinksRaw = t('home.footer.socialLinks', { returnObjects: true });
  const socialLinks = Array.isArray(socialLinksRaw)
    ? (socialLinksRaw as Array<{ icon: string; href: string; label: string }>)
    : [];
  const languages = [
    { code: 'pt-BR', label: t('common.language.pt-BR') },
    { code: 'en-US', label: t('common.language.en-US') },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="w-full border-t pt-16 pb-8 px-6 transition-colors duration-300" style={{ background: 'var(--surface-inverse)', borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.08)' }}>
      <div className="max-w-7xl mx-auto">
        {/* rose top accent */}
        <div className="h-px w-full mb-12" style={{ background: 'linear-gradient(90deg, transparent, rgba(232,54,93,0.35), transparent)' }} />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <img
              src="https://storage.readdy-site.link/project_files/8471bf70-5a6a-4234-ac9f-9bad5a1c4b2b/83ac0fe0-7767-4fd1-b742-a771a87fd226_LOGO-SIG-MOBI-PNG-Fundo-Transparente-1.png?v=a82771fdc6e27836d6f7f323524a524b"
              alt="SIG MOBI"
              className="h-12 w-auto object-contain mb-5"
            />
            <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'var(--text-inverse-muted)' }}>
              {t('home.footer.description')}
            </p>
            <div className="flex items-center gap-4 mt-6">
              {socialLinks.map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-white/10 text-white/50 hover:border-[#00C4F0] hover:text-[#00C4F0] transition-all duration-200 cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${s.icon} text-base`} />
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-4">
              <a href="#nav">{t('home.footer.navigationTitle')}</a>
            </h4>
            <ul className="flex flex-col gap-3">
              {navigationLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
                    className="text-white/50 hover:text-white text-sm transition-colors duration-200 cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Comunidade */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-widest mb-4">
              <a href="#comunidade-links">{t('home.footer.communityTitle')}</a>
            </h4>
            <ul className="flex flex-col gap-3">
              {communityLinks.map((link, i) => (
                <li key={i}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className={`text-sm transition-colors duration-200 cursor-pointer ${link.accent ? 'text-[#00C4F0] hover:text-white font-semibold' : 'text-white/50 hover:text-white'}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t pt-8 flex flex-col gap-6" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex flex-col gap-2">
              <span className="text-white/60 text-xs tracking-wide uppercase">{t('common.language.label')}</span>
              <div className="flex flex-wrap items-center gap-2">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => i18n.changeLanguage(language.code)}
                    className="px-3 py-2 rounded-full text-xs font-semibold transition-colors duration-200"
                    style={{
                      background: i18n.resolvedLanguage === language.code ? '#00C4F0' : 'rgba(255,255,255,0.06)',
                      color: i18n.resolvedLanguage === language.code ? '#091D42' : 'white',
                    }}
                  >
                    {language.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-white/60 text-xs tracking-wide uppercase">{t('common.theme.label')}</span>
              <button
                onClick={toggleTheme}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'white' }}
                aria-label={isDark ? t('common.theme.toggleToLight') : t('common.theme.toggleToDark')}
              >
                <i className={isDark ? 'ri-sun-line' : 'ri-moon-line'} />
                {isDark ? t('common.theme.light') : t('common.theme.dark')}
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-xs text-center md:text-left">
              {t('home.footer.copyright')}
            </p>
            <div className="flex items-center gap-3">
              <span className="text-white/20 text-xs tracking-wide">
                {t('home.footer.poweredByLabel')} <span className="text-white/40 font-medium">{t('home.footer.poweredByValue')}</span>
              </span>
            </div>
            <div className="flex items-center gap-6">
              <a href="#privacy" className="text-white/30 hover:text-white/60 text-xs transition-colors cursor-pointer">
                {t('home.footer.privacy')}
              </a>
              <span className="text-white/10">|</span>
              <a href="#terms" className="text-white/30 hover:text-white/60 text-xs transition-colors cursor-pointer">
                {t('home.footer.terms')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
